import Promise from 'bluebird';
import _ from 'underscore';
import { CSMLAssetSource } from '@bingads-webui-campaign-react/asset-library-model';

// React free utility function
export const uploadVideoMetadata = ({
  videoMetadata,
  savedImages = [],
  savedLogo = undefined,
  newVideoFromMTObject,
  folderRepository,
  imageRepository,
  videoRepository,
  logger,
}) => {
  const { template } = videoMetadata;
  const { scenes } = template;
  // TODO - move to a common place.
  const legacyTemplates = ['00000001-0000-0000-0000-000000000000', '00000001-0000-0000-0000-000000000001'];

  const uploadOriginalImagesPromises = scenes.map((scene) => {
    const imagesToUpload = scene.images;
    if (!imagesToUpload || imagesToUpload.length === 0) {
      return Promise.resolve([]);
    }
    const uploadPromises = imagesToUpload.map((image) => {
      const savedIndex = savedImages.length > 0 ? _.findIndex(savedImages, ({ url }) => url === image) : -1;
      if (savedIndex !== -1) {
        return Promise.resolve(savedImages[savedIndex]);
      }

      return folderRepository.addUrlImageToFolder({
        imageSrc: image,
        sourceType: CSMLAssetSource.Website,
      }).then(imageId => imageRepository.getImage(imageId)).then(imageObj => imageObj);
    });

    return Promise.all(uploadPromises);
  });

  const uploadLogosPromises = scenes.map((scene) => {
    const logosToUpload = scene.logos;
    if (!logosToUpload || logosToUpload.length === 0) {
      return Promise.resolve([]);
    }
    const uploadPromises = logosToUpload.map((logo) => {
      const savedIndex = savedImages.length > 0 ? _.findIndex(savedImages, ({ url }) => url === logo) : -1;
      if (savedIndex !== -1) {
        return Promise.resolve(savedImages[savedIndex]);
      }

      return folderRepository.addUrlImageToFolder({
        imageSrc: logo,
        sourceType: CSMLAssetSource.Website,
      }).then(imageId => imageRepository.getImage(imageId)).then(imageObj => imageObj);
    });

    return Promise.all(uploadPromises);
  });

  const uploadScenesKeyFrames = (scenesInner, savedOriginalImages, savedLogos) => {
    const promises = scenesInner.map((scene, index) => {
      const creativeData = {
        ...scene,
        images: savedOriginalImages[index].map(img => img.url),
        logos: savedLogos[index].map(logo => logo.url),
        url: scene.keyFrame.url,
      };
      let baseId;
      if (savedOriginalImages[index][0]) {
        baseId = savedOriginalImages[index][0].id;
      } else if (savedLogos[index][0]) {
        baseId = savedLogos[index][0].id;
      } else {
        baseId = index;
      }
      return imageRepository.addCreative({
        creativeData,
        baseId,
      }).then((ids) => {
        if (_.isObject(_.first(ids))) {
          throw new Error(ids[0].code);
        }

        return ids[0];
      }).then(id => imageRepository.getImage(id)).then(imageObj => imageObj.url);
    });
    return Promise.all(promises);
  };

  const uploadLogoPromise = () => {
    const { logo } = template;
    if (savedLogo && savedLogo.url !== '' && savedLogo.url === logo.url) {
      return Promise.resolve(savedLogo);
    } else if (logo && logo.url !== '') {
      return folderRepository.addUrlImageToFolder({
        imageSrc: logo.url,
        sourceType: CSMLAssetSource.Website,
      }).then(imageId => imageRepository.getImage(imageId)).then(imageObj => imageObj);
    }
    return Promise.resolve({ url: '', id: '' });
  };

  return Promise.all([...uploadOriginalImagesPromises, ...uploadLogosPromises, uploadLogoPromise()])
    .then((savedImagesArr) => {
      const savedLogoImage = savedImagesArr.pop();
      const savedOriginalImages = savedImagesArr.slice(0, scenes.length);
      const savedLogos = savedImagesArr.slice(scenes.length);

      return uploadScenesKeyFrames(scenes, savedOriginalImages, savedLogos)
        .then((savedKeyFrames) => {
          const videoWithSavedImages = {
            ...videoMetadata,
            template: {
              ...template,
              scenes: scenes.map((scene, index) => ({
                ...scene,
                images: savedOriginalImages[index] ? savedOriginalImages[index].map(img => img.url) : [],
                logos: savedLogos[index] ? savedLogos[index].map(logo => logo.url) : [],
                keyFrame: {
                  ...scene.keyFrame,
                  url: savedKeyFrames[index],
                },
              })),
              logo: {
                ...template.logo,
                url: savedLogoImage.url,
              },
            },
          };

          const { templateId } = videoWithSavedImages.template;
          const sourceType = legacyTemplates.includes(templateId) ? null : 'AIGenerated';
          if (logger && logger.logTrace) {
            logger.logTrace(`[VideoMetadataUtil] CreateVideo, sourceType:${sourceType}, templateId:${templateId}`);
          }
          return videoRepository.addVideoCreative({ videoCreative: videoWithSavedImages, sourceType })
            .then((id) => {
              const videoWidth = videoWithSavedImages && videoWithSavedImages.template && videoWithSavedImages.template.width;
              const videoHeight = videoWithSavedImages && videoWithSavedImages.template && videoWithSavedImages.template.height;
              const isVertical = videoHeight > videoWidth;
              const fromMT = newVideoFromMTObject({ Id: id, SubType: isVertical ? 'VideoMedia9X16' : 'VideoMedia16X9', Status: 'Draft' });
              const appliedVideoWithId = {
                ...videoWithSavedImages,
                id: fromMT.id,
                idString: fromMT.idString,
                type: fromMT.type,
                status: fromMT.status,
              };
              return appliedVideoWithId;
            });
        });
    });
};
